import React, { useEffect, useState, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { getApiBase, getAuthHeaders } from '../../utils/api';
import { useAuth } from '../../contexts/AuthContext';
import { useComponentEvents, useTodoEvents, useUrbanrideEvents, SSEEventType } from '../../contexts/SSEContext';
import { cancelTrip } from '../../services/urbanrideService';
import Timeline2 from './Timeline2';
import ExecutivesTab from './ExecutivesTab';
import GeneralTab from './GeneralTab';
import AddComponentModal from './AddComponentModal';
import EditComponentModal from './EditComponentModal';
import AssignExecutiveModal from './AssignExecutiveModal';
import ComponentDeleteModal from './ComponentDeleteModal';
import TripBookingModal from './TripBookingModal';
import ManageTripPermissionsModal from './ManageTripPermissionsModal';
import TodoModal from './TodoModal';
import CreateEmailAddressModal from './CreateEmailAddressModal';
import EmailAddressList from './EmailAddressList';

const TripDetails = ({ tripId: propTripId }) => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [trip, setTrip] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [activeTab, setActiveTab] = useState('timeline2'); // general | timeline2 | timeline | components | executives | notes | todos | email
  const [showAddComponentModal, setShowAddComponentModal] = useState(false);
  const [editComponent, setEditComponent] = useState(null);
  const [showAssignExecutiveModal, setShowAssignExecutiveModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [componentToDelete, setComponentToDelete] = useState(null);
  const [showBookingModal, setShowBookingModal] = useState(false);
  const [showPermissionsModal, setShowPermissionsModal] = useState(false);
  const [showEmailModal, setShowEmailModal] = useState(false);
  const [todos, setTodos] = useState([]);
  const [todoLoading, setTodoLoading] = useState(false);
  const [showTodoModal, setShowTodoModal] = useState(false);
  const [editingTodo, setEditingTodo] = useState(null);
  const [users, setUsers] = useState([]);
  const [allExecutives, setAllExecutives] = useState([]);
  const [tripExecutives, setTripExecutives] = useState([]);
  const [allTrips, setAllTrips] = useState([]); // All trips for reassign dropdown

  const tripId = useMemo(() => {
    if (propTripId) return propTripId;
    try { return new URLSearchParams(window.location.search).get('id'); } catch { return null; }
  }, [propTripId]);

  const apiBase = getApiBase();
  const headers = getAuthHeaders();

  const refreshTrip = useCallback(async () => {
    if (!tripId) return;
    setLoading(true);
    setError('');
    try {
      const res = await axios.get(`${apiBase}/trips/${tripId}`, { headers });
      setTrip(res.data || null);
    } catch (e) {
      setError('Failed to load trip details');
    } finally {
      setLoading(false);
    }
  }, [tripId, apiBase, headers]);

  const loadTodos = async () => {
    if (!tripId) return;
    setTodoLoading(true);
    try {
      const res = await axios.get(`${apiBase}/todos`, { params: { trip_id: tripId }, headers });
      setTodos(Array.isArray(res.data) ? res.data : []);
    } catch (e) {
      setTodos([]);
    } finally {
      setTodoLoading(false);
    }
  };

  const loadUsers = async () => {
    try {
      const res = await axios.get(`${apiBase}/users`, { headers });
      setUsers(Array.isArray(res.data) ? res.data : []);
    } catch (e) {
      console.error('Failed to load users', e);
    }
  };

  const loadAllTrips = async () => {
    try {
      const res = await axios.get(`${apiBase}/trips`, { params: { limit: 100 }, headers });
      const tripsData = res.data?.trips || res.data || [];
      setAllTrips(Array.isArray(tripsData) ? tripsData : []);
    } catch (e) {
      console.error('Failed to load all trips', e);
    }
  };

  const loadExecutives = async () => {
    try {
      // Load all executives
      const res = await axios.get(`${apiBase}/executives`, { headers });
      const executives = Array.isArray(res.data) ? res.data : [];
      setAllExecutives(executives);
      
      // Filter executives assigned to this trip
      if (trip && trip.executive_ids) {
        const assigned = executives.filter(exec => trip.executive_ids.includes(exec.id));
        setTripExecutives(assigned);
      } else {
        // Fallback: try to match by names if no IDs
        if (trip && trip.executive_names) {
          const assigned = executives.filter(exec => {
            const fullName = exec.full_name || `${exec.first_name} ${exec.last_name}`;
            return trip.executive_names.includes(fullName);
          });
          setTripExecutives(assigned);
        } else {
          setTripExecutives([]);
        }
      }
    } catch (e) {
      console.error('Failed to load executives:', e);
      setAllExecutives([]);
      setTripExecutives([]);
    }
  };

  useEffect(() => { refreshTrip(); /* eslint-disable-next-line */ }, [tripId]);
  useEffect(() => { loadAllTrips(); /* eslint-disable-next-line */ }, []); // Load all trips for reassign dropdown
  useEffect(() => {
    if (activeTab === 'todos') {
      loadTodos();
      loadUsers();
    }
    /* eslint-disable-next-line */
  }, [activeTab, tripId]);
  useEffect(() => { if (trip) loadExecutives(); /* eslint-disable-next-line */ }, [trip]);

  // Handle SSE events for trip components (real-time updates)
  const handleComponentEvent = useCallback((event) => {
    const eventTripId = event.trip_id;
    console.log('TripDetails SSE: Received component event', {
      type: event.type,
      eventTripId,
      currentTripId: tripId,
      match: eventTripId === tripId
    });
    // Only process events for this trip
    if (!tripId || eventTripId !== tripId) {
      console.log('TripDetails SSE: Ignoring event - trip_id mismatch or no tripId');
      return;
    }
    // Refresh trip data to get the new component
    console.log('TripDetails SSE: Refreshing trip data for', tripId);
    refreshTrip();
  }, [tripId, refreshTrip]);

  useComponentEvents(handleComponentEvent, [tripId, refreshTrip]);

  // SSE: Handle Urbanride webhook events (chauffeur assigned, vehicle status, etc.)
  const handleUrbanrideEvent = useCallback((event) => {
    const eventTripId = event.data?.trip_id || event.trip_id;
    if (!tripId || eventTripId !== tripId) return;
    refreshTrip();
  }, [tripId, refreshTrip]);

  useUrbanrideEvents(handleUrbanrideEvent, [tripId, refreshTrip]);

  // Handle SSE events for todos (real-time updates)
  const handleTodoEvent = useCallback((event) => {
    const todoData = event.data?.todo;
    // Only process events for this trip's todos
    if (todoData?.trip_id !== tripId) return;

    if (event.type === SSEEventType.TODO_CREATED && todoData) {
      setTodos((prev) => {
        if (prev.some((t) => t.id === todoData.id)) return prev;
        return [...prev, todoData];
      });
    } else if (event.type === SSEEventType.TODO_UPDATED && todoData) {
      setTodos((prev) =>
        prev.map((t) => (t.id === todoData.id ? { ...t, ...todoData } : t))
      );
    } else if (event.type === SSEEventType.TODO_DELETED && todoData) {
      setTodos((prev) => prev.filter((t) => t.id !== todoData.id));
    }
  }, [tripId]);

  useTodoEvents(handleTodoEvent, [tripId]);

  // Handle editComponent query parameter for deep linking to edit modal
  useEffect(() => {
    if (!trip || !trip.components) return;

    try {
      const params = new URLSearchParams(window.location.search);
      const componentIdToEdit = params.get('editComponent');

      if (componentIdToEdit) {
        // Find the component in the trip
        const component = trip.components.find(c => c.id === componentIdToEdit);

        if (component) {
          // Set the component to edit, which will open the edit modal
          setEditComponent(component);

          // Remove the query parameter from URL without reloading
          const newUrl = window.location.pathname;
          window.history.replaceState({}, '', newUrl);
        } else {
          console.warn(`Component with ID ${componentIdToEdit} not found in trip`);
        }
      }
    } catch (error) {
      console.error('Error parsing editComponent query parameter:', error);
    }
    /* eslint-disable-next-line */
  }, [trip]);

  const handleEditComponent = (component) => setEditComponent(component);
  const handleDeleteComponent = async (componentOrId) => {
    // Handle both string ID and component object (from Timeline2/TimelineComponentCard)
    const componentId = typeof componentOrId === 'string' ? componentOrId : componentOrId?.id;

    if (!tripId || !componentId) return;

    // Find the component to delete
    const component = components.find(c => c.id === componentId);
    if (!component) {
      alert('Component not found');
      return;
    }

    // Show delete confirmation modal
    setComponentToDelete(component);
    setShowDeleteModal(true);
  };

  const handleReassignComponent = async (component, targetTripId) => {
    if (!tripId || !component?.id || !targetTripId) return;

    try {
      const formData = new FormData();
      formData.append('target_trip_id', targetTripId);
      // Remove Content-Type to let axios set it correctly for FormData
      const reassignHeaders = { ...headers };
      delete reassignHeaders['Content-Type'];

      await axios.post(
        `${apiBase}/trips/${tripId}/components/${component.id}/reassign`,
        formData,
        { headers: reassignHeaders }
      );

      // Refresh the current trip to reflect the removal
      refreshTrip();
      // Reload all trips to update the list
      loadAllTrips();
    } catch (err) {
      console.error('Error reassigning component:', err);
      alert('Failed to move component to another trip');
    }
  };

  const confirmDeleteComponent = async () => {
    if (!componentToDelete) return;

    try {
      // Check if this is a chauffeur_service component with an Urbanride booking
      const isChauffeuredService = componentToDelete.component_type === 'chauffeur_service';
      const componentData = componentToDelete.component_data || {};
      const urcn = componentToDelete.confirmation_number || componentData.urbanride_urcn || componentData.confirmation_number;

      if (isChauffeuredService && urcn) {
        // Attempt to cancel the Urbanride booking via API
        try {
          await cancelTrip(urcn);
        } catch (cancelError) {
          // Log the error but continue with deletion
          // The booking might already be cancelled, expired, or API not configured
          console.warn(`Failed to cancel Urbanride booking ${urcn}:`, cancelError.message || cancelError);
        }

        // Clean up urbanride_bookings references from linked components
        const linkedComponentIds = componentData.linked_component_ids || [];
        if (linkedComponentIds.length > 0) {
          await cleanupUrbanrideReferences(linkedComponentIds, urcn);
        }
      }

      // Delete the component from the trip
      await axios.delete(`${apiBase}/trips/${tripId}/components/${componentToDelete.id}`, { headers });
      await refreshTrip();
      setShowDeleteModal(false);
      setComponentToDelete(null);
    } catch (e) {
      alert('Failed to delete component: ' + (e.response?.data?.detail || e.message));
      setShowDeleteModal(false);
      setComponentToDelete(null);
    }
  };

  // Helper function to clean up urbanride_bookings references from linked components
  const cleanupUrbanrideReferences = async (linkedComponentIds, urcnToRemove) => {
    // Get the current trip components from state
    const currentComponents = Array.isArray(trip?.components) ? trip.components : [];

    for (const componentId of linkedComponentIds) {
      try {
        // Find the component in the current trip's components array
        const component = currentComponents.find(c => c.id === componentId);

        if (!component) {
          console.warn(`Component ${componentId} not found in trip - may have been deleted`);
          continue;
        }

        const existingData = component.component_data || {};

        // Filter out the booking with this URCN from urbanride_bookings array
        const existingBookings = existingData.urbanride_bookings || [];
        const updatedBookings = existingBookings.filter(booking => booking.urcn !== urcnToRemove);

        // Also check legacy urbanride_booking_urcn field
        const legacyUrcn = existingData.urbanride_booking_urcn;
        const shouldClearLegacy = legacyUrcn === urcnToRemove;

        // Only update if there are changes to make
        if (updatedBookings.length !== existingBookings.length || shouldClearLegacy) {
          const updatePayload = {
            component_data: {
              ...existingData,
              urbanride_bookings: updatedBookings
            }
          };

          // Clear legacy fields if they match the cancelled URCN
          if (shouldClearLegacy) {
            updatePayload.component_data.urbanride_booking_urcn = null;
            updatePayload.component_data.urbanride_booking_mode = null;
            updatePayload.component_data.urbanride_booking_leg_indices = null;
          }

          await axios.put(`${apiBase}/trips/${tripId}/components/${componentId}`, updatePayload, { headers });
        }
      } catch (cleanupError) {
        // Log error but don't fail the entire operation
        console.warn(`Failed to cleanup Urbanride reference from component ${componentId}:`, cleanupError.message);
      }
    }
  };

  const cancelDeleteComponent = () => {
    setShowDeleteModal(false);
    setComponentToDelete(null);
  };

  const handleDeleteTodo = async (todoId) => {
    if (!window.confirm('Are you sure you want to delete this todo?')) {
      return;
    }

    try {
      await axios.delete(`${apiBase}/todos/${todoId}`, { headers });
      loadTodos();
    } catch (err) {
      alert('Failed to delete todo: ' + (err.response?.data?.detail || err.message));
    }
  };

  const handleUpdateTodo = async (todoId, updateData) => {
    try {
      await axios.put(`${apiBase}/todos/${todoId}`, updateData, { headers });
      loadTodos();
    } catch (err) {
      alert('Failed to update todo: ' + (err.response?.data?.detail || err.message));
    }
  };

  const handleToggleTodoStatus = (todo) => {
    const newStatus = todo.status === 'completed' ? 'open' : 'completed';
    handleUpdateTodo(todo.id, { status: newStatus });
  };

  const handleTodoModalClose = () => {
    setShowTodoModal(false);
    setEditingTodo(null);
  };

  const handleTodoModalSuccess = () => {
    loadTodos();
    handleTodoModalClose();
  };

  const handleEditTodo = (todo) => {
    setEditingTodo(todo);
    setShowTodoModal(true);
  };

  const getPriorityBadge = (priority) => {
    switch (priority) {
      case 'high': return { text: 'High', color: 'bg-red-100 text-red-700' };
      case 'medium': return { text: 'Med', color: 'bg-yellow-100 text-yellow-700' };
      case 'low': return { text: 'Low', color: 'bg-green-100 text-green-700' };
      default: return { text: priority, color: 'bg-gray-100 text-gray-700' };
    }
  };

  const handleAssignExecutives = () => {
    setShowAssignExecutiveModal(true);
  };

  const handleRemoveExecutiveFromTrip = async (executiveName) => {
    if (!window.confirm(`Remove ${executiveName} from this trip?`)) return;
    
    try {
      // Remove executive from trip by filtering out the name and corresponding ID
      const updatedNames = (trip.executive_names || []).filter(name => name !== executiveName);
      
      // Also update executive_ids if they exist
      let updatedIds = trip.executive_ids || [];
      if (trip.executive_ids && allExecutives.length > 0) {
        // Find the executive ID by matching the name
        const executiveToRemove = allExecutives.find(exec => {
          const fullName = exec.full_name || `${exec.first_name} ${exec.last_name}`;
          return fullName === executiveName;
        });
        
        if (executiveToRemove) {
          updatedIds = trip.executive_ids.filter(id => id !== executiveToRemove.id);
        }
      }

      await axios.put(`${apiBase}/trips/${tripId}`, {
        ...trip,
        executive_names: updatedNames,
        executive_ids: updatedIds
      }, { headers });
      
      await refreshTrip();
    } catch (e) {
      alert('Failed to remove executive from trip: ' + (e.response?.data?.detail || e.message));
    }
  };

  const handleViewExecutiveProfile = (executiveName) => {
    // Find the executive by name to get their ID
    const executive = tripExecutives.find(exec => {
      const fullName = exec.full_name || `${exec.first_name} ${exec.last_name}`;
      return fullName === executiveName;
    }) || allExecutives.find(exec => {
      const fullName = exec.full_name || `${exec.first_name} ${exec.last_name}`;
      return fullName === executiveName;
    });

    if (executive && executive.id) {
      navigate(`/executives?id=${executive.id}`);
    } else {
      // Fallback: navigate to executives page without specific ID
      navigate('/executives');
    }
  };

  if (!tripId) return <div className="p-4 text-red-600">No trip selected.</div>;
  if (loading) return <div className="p-4">Loading trip…</div>;
  if (error) return <div className="p-4 text-red-600">{error}</div>;
  if (!trip) return <div className="p-4">Trip not found.</div>;

  const components = Array.isArray(trip.components) ? trip.components : [];

  // Simple helpers for ComponentsTab
  const getStatusColor = (status) => {
    const s = String(status || 'pending').toLowerCase();
    if (s.includes('confirm')) return 'text-green-700 bg-green-50 border-green-200';
    if (s.includes('cancel')) return 'text-red-700 bg-red-50 border-red-200';
    if (s.includes('progress')) return 'text-yellow-700 bg-yellow-50 border-yellow-200';
    return 'text-slate-700 bg-slate-50 border-slate-200';
  };
  const getComponentTypeName = (t) => ({
    hotel: 'Hotel',
    meeting: 'Meeting',
    speaker_meeting: 'Speaker Meeting',
    restaurant: 'Restaurant',
    private_flight: 'Private Flight',
    commercial_flight: 'Commercial Flight',
    rental_car: 'Rental Car',
    general: 'General',
    chauffeured_service: 'Chauffeured Service',
    event: 'Event',
    bundle: 'Bundle'
  }[t] || (t || 'Component'));

  const componentTypeStyles = {
    hotel: { backgroundColor: '#EEF2FF', borderColor: '#C7D2FE', nodeColor: '#818CF8', iconColor: '#4338CA', badgeColor: 'text-indigo-700 bg-indigo-50 border-indigo-200' },
    meeting: { backgroundColor: '#ECFDF5', borderColor: '#A7F3D0', nodeColor: '#34D399', iconColor: '#065F46', badgeColor: 'text-emerald-700 bg-emerald-50 border-emerald-200' },
    speaker_meeting: { backgroundColor: '#FFF1F2', borderColor: '#FECDD3', nodeColor: '#FB7185', iconColor: '#9F1239', badgeColor: 'text-rose-700 bg-rose-50 border-rose-200' },
    restaurant: { backgroundColor: '#FFF7ED', borderColor: '#FED7AA', nodeColor: '#FB923C', iconColor: '#9A3412', badgeColor: 'text-amber-700 bg-amber-50 border-amber-200' },
    private_flight: { backgroundColor: '#F5F3FF', borderColor: '#DDD6FE', nodeColor: '#A78BFA', iconColor: '#6D28D9', badgeColor: 'text-violet-700 bg-violet-50 border-violet-200' },
    commercial_flight: { backgroundColor: '#EFF6FF', borderColor: '#BFDBFE', nodeColor: '#60A5FA', iconColor: '#1D4ED8', badgeColor: 'text-blue-700 bg-blue-50 border-blue-200' },
    rental_car: { backgroundColor: '#F0FDF4', borderColor: '#BBF7D0', nodeColor: '#86EFAC', iconColor: '#166534', badgeColor: 'text-green-700 bg-green-50 border-green-200' },
    general: { backgroundColor: '#F8FAFC', borderColor: '#E2E8F0', nodeColor: '#94A3B8', iconColor: '#334155', badgeColor: 'text-slate-700 bg-slate-50 border-slate-200' },
    chauffeured_service: { backgroundColor: '#FEF3C7', borderColor: '#FCD34D', nodeColor: '#FBBF24', iconColor: '#92400E', badgeColor: 'text-yellow-700 bg-yellow-50 border-yellow-200' },
    event: { backgroundColor: '#FCE7F3', borderColor: '#F9A8D4', nodeColor: '#EC4899', iconColor: '#BE185D', badgeColor: 'text-pink-700 bg-pink-50 border-pink-200' }
  };

  return (
    <div className="p-4 space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-semibold">{trip.title || 'Trip'}</h2>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={() => setShowPermissionsModal(true)} className="px-3 py-1.5 rounded bg-purple-600 text-white hover:bg-purple-700">Manage Permissions</button>
          <button onClick={() => setShowAddComponentModal(true)} className="px-3 py-1.5 rounded bg-indigo-600 text-white hover:bg-indigo-700">Add Component</button>
          <button onClick={() => setShowBookingModal(true)} className="px-3 py-1.5 rounded bg-green-600 text-white hover:bg-green-700">Book</button>
          <button onClick={refreshTrip} className="px-3 py-1.5 rounded border">Refresh</button>
        </div>
      </div>

      {/* Tabs */}
      <div className="border-b">
        <nav className="-mb-px flex gap-6 flex-wrap">
          {[
            { key: 'general', label: 'General' },
            { key: 'timeline2', label: 'Timeline' },
            { key: 'executives', label: 'Executives' },
            { key: 'email', label: 'Email' },
            { key: 'notes', label: 'Notes' },
            { key: 'todos', label: 'Todos' }
          ].map(tab => (
            <button
              key={tab.key}
              className={`py-2 px-1 border-b-2 text-sm font-medium ${
                activeTab === tab.key
                  ? 'border-indigo-600 text-indigo-700'
                  : 'border-transparent text-slate-600 hover:text-slate-800'
              }`}
              onClick={() => setActiveTab(tab.key)}
            >
              {tab.label}
            </button>
          ))}
        </nav>
      </div>

      {/* Tab Panels */}
      {activeTab === 'general' && (
        <GeneralTab
          trip={trip}
          onTripUpdate={setTrip}
        />
      )}

      {activeTab === 'timeline2' && (
        <Timeline2
          trip={trip}
          components={components}
          executives={tripExecutives}
          allExecutives={allExecutives}
          onTripUpdate={setTrip}
          onEditComponent={handleEditComponent}
          onDeleteComponent={handleDeleteComponent}
          onReassignComponent={handleReassignComponent}
          allTrips={allTrips}
          onRefreshComponents={refreshTrip}
        />
      )}

      {activeTab === 'executives' && (
        <div className="bg-white rounded border p-4">
          <div className="flex items-center justify-between mb-4">
            <div className="font-medium">Trip Executives</div>
            <div className="flex gap-2">
              <button 
                onClick={handleAssignExecutives}
                className="text-sm bg-blue-50 hover:bg-blue-100 text-blue-600 px-3 py-1 rounded"
              >
                Assign Executive
              </button>
              <button 
                onClick={() => window.open('/executives', '_blank')}
                className="text-sm bg-green-50 hover:bg-green-100 text-green-600 px-3 py-1 rounded"
              >
                Add New Executive
              </button>
            </div>
          </div>
          
          {(trip.executive_names || []).length === 0 ? (
            <div className="text-center py-8 border-2 border-dashed border-slate-200 rounded-lg">
              <div className="text-slate-500 mb-2">No executives assigned to this trip</div>
              <button 
                onClick={handleAssignExecutives}
                className="text-blue-600 hover:text-blue-700 text-sm"
              >
                Assign an executive to this trip
              </button>
            </div>
          ) : (
            <div className="space-y-3">
              {(trip.executive_names || []).map((name, idx) => (
                <div key={idx} className="flex items-center justify-between p-3 border border-slate-200 rounded-lg hover:bg-slate-50">
                  <div className="flex items-center">
                    <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center mr-3">
                      <span className="text-blue-600 text-sm font-semibold">
                        {name.charAt(0).toUpperCase()}
                      </span>
                    </div>
                    <div>
                      <div className="font-medium text-slate-800">{name}</div>
                      <div className="text-sm text-slate-500">Executive</div>
                    </div>
                  </div>
                  <div className="flex gap-2">
                    <button
                      onClick={() => handleViewExecutiveProfile(name)}
                      className="text-sm text-blue-600 hover:text-blue-700"
                    >
                      View Profile
                    </button>
                    <button
                      onClick={() => handleRemoveExecutiveFromTrip(name)}
                      className="text-sm text-red-600 hover:text-red-700"
                    >
                      Remove
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {activeTab === 'email' && (
        <div className="bg-white rounded border p-6">
          <div className="flex items-center justify-between mb-6">
            <div>
              <h3 className="text-lg font-semibold text-slate-800">Email Addresses for This Trip</h3>
              <p className="text-sm text-slate-600 mt-1">
                Create dedicated email addresses to receive travel confirmations directly into this trip
              </p>
            </div>
            <button
              onClick={() => setShowEmailModal(true)}
              className="flex items-center gap-2 px-4 py-2 bg-violet-600 hover:bg-violet-700 text-white rounded-lg transition-colors"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
              </svg>
              Create Email Address
            </button>
          </div>

          <EmailAddressList
            tripId={tripId}
            onEmailCreated={() => {}}
          />

          <div className="mt-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
            <h4 className="text-sm font-semibold text-blue-900 mb-2">How it works:</h4>
            <ol className="text-sm text-blue-800 space-y-1.5 list-decimal list-inside">
              <li>Create a dedicated email address for this trip using the button above</li>
              <li>Forward travel confirmation emails (flights, hotels, etc.) to this address</li>
              <li>Attach PDF, images, or documents containing the confirmation details</li>
              <li>Confirmations will automatically be parsed and added to this trip's components</li>
            </ol>
          </div>
        </div>
      )}

      {activeTab === 'notes' && (
        <div className="bg-white rounded border p-4 text-sm text-slate-700">
          <div className="font-medium mb-2">Notes</div>
          <div className="text-slate-600">No trip-level notes field configured. Add notes to individual components for now.</div>
        </div>
      )}

      {activeTab === 'todos' && (
        <div className="bg-white rounded border p-4">
          <div className="flex items-center justify-between mb-4">
            <div className="font-medium text-lg">Trip Todos</div>
            <button
              onClick={() => setShowTodoModal(true)}
              className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-md flex items-center gap-2 text-sm"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
              </svg>
              Create Todo
            </button>
          </div>

          {todoLoading ? (
            <div className="text-center py-8">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
              <p className="text-gray-600 mt-2">Loading todos...</p>
            </div>
          ) : todos.length === 0 ? (
            <div className="text-center py-12 bg-gray-50 rounded-lg">
              <svg className="w-16 h-16 text-gray-400 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
              </svg>
              <p className="text-gray-600 font-medium">No todos for this trip</p>
              <p className="text-gray-500 text-sm mt-1">Create your first todo to get started</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200 border border-gray-200 rounded-lg">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-3 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider w-10">
                      Done
                    </th>
                    <th className="px-3 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider w-16">
                      Priority
                    </th>
                    <th className="px-3 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">
                      Title
                    </th>
                    <th className="px-3 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">
                      Assigned To
                    </th>
                    <th className="px-3 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">
                      Created By
                    </th>
                    <th className="px-3 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider w-28">
                      Due Date
                    </th>
                    <th className="px-3 py-3 text-center text-xs font-medium text-gray-700 uppercase tracking-wider w-20">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {todos.map(todo => {
                    const isOverdue = todo.due_date && new Date(todo.due_date) < new Date() && todo.status !== 'completed';
                    const priorityBadge = getPriorityBadge(todo.priority);

                    return (
                      <tr key={todo.id} className={`hover:bg-gray-50 ${todo.status === 'completed' ? 'opacity-60' : ''}`}>
                        <td className="px-3 py-3 whitespace-nowrap">
                          <button
                            onClick={() => handleToggleTodoStatus(todo)}
                            className="text-lg hover:scale-110 transition-transform"
                            title={`Mark as ${todo.status === 'completed' ? 'open' : 'completed'}`}
                          >
                            {todo.status === 'completed' ? '✅' : '⬜'}
                          </button>
                        </td>
                        <td className="px-3 py-3 whitespace-nowrap">
                          <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${priorityBadge.color}`}>
                            {priorityBadge.text}
                          </span>
                        </td>
                        <td className="px-3 py-3">
                          <div className={todo.status === 'completed' ? 'line-through text-gray-500' : ''}>
                            <div className="text-sm font-medium text-gray-900">{todo.title}</div>
                            {todo.description && (
                              <div className="text-xs text-gray-500 mt-0.5 truncate max-w-md" title={todo.description}>
                                {todo.description}
                              </div>
                            )}
                          </div>
                        </td>
                        <td className="px-3 py-3 whitespace-nowrap text-sm text-gray-700">
                          {todo.assigned_to_name || <span className="text-gray-400 italic">Unassigned</span>}
                        </td>
                        <td className="px-3 py-3 whitespace-nowrap text-sm text-gray-700">
                          {todo.created_by_name || '-'}
                        </td>
                        <td className="px-3 py-3 whitespace-nowrap text-sm text-gray-700">
                          {todo.due_date ? (
                            <div className={isOverdue ? 'text-red-600 font-semibold' : ''}>
                              {new Date(todo.due_date).toLocaleDateString()}
                              {isOverdue && <div className="text-xs">Overdue!</div>}
                            </div>
                          ) : (
                            <span className="text-gray-400">-</span>
                          )}
                        </td>
                        <td className="px-3 py-3 whitespace-nowrap text-center">
                          <div className="flex items-center justify-center gap-1">
                            <button
                              onClick={() => handleEditTodo(todo)}
                              className="text-gray-400 hover:text-blue-600 p-1"
                              title="Edit todo"
                            >
                              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                              </svg>
                            </button>
                            <button
                              onClick={() => handleDeleteTodo(todo.id)}
                              className="text-gray-400 hover:text-red-600 p-1"
                              title="Delete todo"
                            >
                              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                              </svg>
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* Modals */}
      {showAddComponentModal && (
        <AddComponentModal
          tripId={tripId}
          onClose={() => setShowAddComponentModal(false)}
          onSuccess={async () => { setShowAddComponentModal(false); await refreshTrip(); }}
          tripExecutives={tripExecutives}
          allExecutives={allExecutives}
          meetingExecutives={tripExecutives}
          allContacts={[]}
          tripComponents={trip?.components || []}
        />
      )}

      {editComponent && (
        <EditComponentModal
          component={editComponent}
          onClose={() => setEditComponent(null)}
          onSuccess={async () => { setEditComponent(null); await refreshTrip(); }}
          tripId={tripId}
          tripExecutives={tripExecutives}
          allExecutives={allExecutives}
          allContacts={[]}
          tripComponents={trip?.components || []}
        />
      )}

      {showAssignExecutiveModal && (
        <AssignExecutiveModal
          trip={trip}
          onClose={() => setShowAssignExecutiveModal(false)}
          onUpdate={refreshTrip}
        />
      )}

      {showDeleteModal && componentToDelete && (
        <ComponentDeleteModal
          component={componentToDelete}
          trip={trip}
          onConfirm={confirmDeleteComponent}
          onCancel={cancelDeleteComponent}
        />
      )}

      {showBookingModal && (
        <TripBookingModal
          tripId={tripId}
          trip={trip}
          onClose={() => setShowBookingModal(false)}
          onSuccess={refreshTrip}
        />
      )}

      {showPermissionsModal && (
        <ManageTripPermissionsModal
          trip={trip}
          currentUser={user}
          onClose={() => setShowPermissionsModal(false)}
          onUpdate={refreshTrip}
        />
      )}

      {showTodoModal && (
        <TodoModal
          todo={editingTodo}
          users={users}
          defaultTripId={tripId}
          onClose={handleTodoModalClose}
          onSuccess={handleTodoModalSuccess}
        />
      )}

      {showEmailModal && trip && (
        <CreateEmailAddressModal
          open={showEmailModal}
          onOpenChange={setShowEmailModal}
          type="trip"
          tripId={tripId}
          tripTitle={trip.title}
          onSuccess={() => {
            // Refresh to update email list
            refreshTrip();
          }}
        />
      )}
    </div>
  );
};

export default TripDetails;